import React, {useEffect, useState} from "react";
import TextareaAutosize from "react-textarea-autosize";
import {useNavigate} from "react-router-dom";
import {Controller, useForm} from "react-hook-form";
import "./Add.scss";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {
  getUserGroupsAsync,
  getUserTypesAsync,
  selectStatus,
  selectUserGroups,
  selectUserGroupsStatus,
  selectUserTypes,
  selectUserTypesStatus,
  selectAddValidationErrors,
  clearValidation
} from "./addSlice";
import {SpinnerButton} from "../../button/SpinnerButton";
import {Modal} from "../../modal/Modal";
import {sortByDescription, sortUserGroups} from "../sort";
import {validateFileInput} from "../validate";
import {isAnyPairAvailable, getFullPath, isPairUnavailable} from "../mappers";
import {type Configuration, ConfigurationFieldName, type UserGroup} from "../types";
import {fetchListAsync, selectConfigurations} from "../list/listSlice";
import {
  applicabilityMatrixError,
  applicablityAccessError,
  defaultUserGroupDescription,
  descriptionMaxLength,
  descriptionMaxLengthValidation,
  genericError,
  nameMaxLength,
  nameMaxLengthValidation,
  requiredFieldValidation
} from "../constants";
import {ValidationMessage} from "../common/ValidationMessage";
import {hasValidationMessage} from "../common/hasValidationMessage";
import {addFailedText} from "./constants";
import {AddUserGroup} from "../common/AddUserGroup";
import {isFailed, isLoading} from "../../../infrastructure/status";
import {displayUserMessage} from "../../message-panel/messagesSlice";
import {errorUserMessage} from "../../message-panel/actions";
import {hasConfigurationChanged, onAdd} from "./helpers";
import {deselectAll, selectAll} from "../common/selectDeselectAll";
import {onUserTypeCheckboxChange} from "../common/onUserTypeCheckboxChange";
import {resetValidation} from "./hooks";
import {noAvailableUserGroups} from "../common/constants";

export const Add = () => {
  const {
    register,
    handleSubmit,
    formState: {errors = {}},
    control,
    reset,
    getValues,
    clearErrors,
    setValue
  } = useForm<Configuration>();

  const navigate = useNavigate();
  const status = useAppSelector(selectStatus);
  const allConfigurations = useAppSelector(selectConfigurations) ?? [];
  const dispatch = useAppDispatch();
  const [isAddUserGroupDirty, setIsAddUserGroupDirty] = useState<boolean>(false);

  useEffect(() => {
    dispatch(clearValidation({}));
    dispatch(fetchListAsync());
    dispatch(getUserTypesAsync());
    dispatch(getUserGroupsAsync());
  }, [dispatch]);

  const values = getValues();

  resetValidation(values, reset);

  const hasChanged = hasConfigurationChanged(values) || isAddUserGroupDirty;

  const userGroups = sortUserGroups(useAppSelector(selectUserGroups));
  const userGroupsStatus = useAppSelector(selectUserGroupsStatus);
  const userTypes = sortByDescription(useAppSelector(selectUserTypes));
  const userTypesStatus = useAppSelector(selectUserTypesStatus);
  const clearValidationErrors = () => {
    dispatch(clearValidation({name: "typeGroupPairs"}));
  };

  const [newUserGroups, setNewUserGroups] = useState<UserGroup[]>([]);

  const allUserGroups = [...userGroups, ...newUserGroups];

  const unavailableTypeGroupPairs = allConfigurations
    .flatMap(configuration => configuration.typeGroupPairs)
    .filter(
      typeGroupPair =>
        allUserGroups.find(userGroup => userGroup.code === typeGroupPair.groupId) &&
        userTypes.find(userType => userType.code === typeGroupPair.typeId)
    );

  const validationErrors = useAppSelector(selectAddValidationErrors);

  const onSubmit = onAdd(dispatch, navigate);

  const onSubmitFailure = () => {
    dispatch(displayUserMessage(errorUserMessage(addFailedText).payload));
  };

  const goBack = () => {
    navigate(-1);
  };

  const cancelChanges = () => {
    !hasChanged && goBack();
  };

  const isLoadingApplicability = isLoading(userGroupsStatus) || isLoading(userTypesStatus);
  const hasLoadingApplicabilityFailed = isFailed(userTypesStatus) || isFailed(userGroupsStatus);
  const hasAnyPairAvailable = isAnyPairAvailable(
    unavailableTypeGroupPairs,
    allUserGroups,
    userTypes
  );

  const typeGroupEntries = Object.entries(values).filter(entry => entry[0].startsWith("type_"));

  const areAllTypeGroupPairsSelected = !typeGroupEntries.some(entry => !entry[1]);

  const setNewUserGroupsWrapper = (userGroups: UserGroup[]) => {
    setNewUserGroups(userGroups);
    const existingPairs = allConfigurations.flatMap(configuration => configuration.typeGroupPairs);
    const groupId = userGroups[userGroups.length - 1].code;
    userTypes.forEach(userType => {
      !existingPairs.find(pair => pair.typeId === userType.code && pair.groupId === groupId) &&
        setValue(`type_${userType.code}_group_${groupId}`, false);
    });
  };

  return (
    <div className="container">
      <div className="configurations-container title">
        <h2>Add configuration</h2>
      </div>
      {isLoadingApplicability && (
        <div className="loader-wrap light-background">
          <i className="fad fa-spinner-third fa-spin icon-xxl spinner spinner-page dark"></i>
        </div>
      )}
      {!isLoadingApplicability && (
        <div className="configurations-container">
          <div id="form-inputs">
            <form className="input-dark">
              <div className="form-group medium">
                <label htmlFor="name" className="required">
                  Name
                </label>
                <Controller
                  control={control}
                  name={ConfigurationFieldName.name}
                  rules={{required: true, maxLength: nameMaxLength}}
                  render={({field: {onChange, onBlur, value}}) => {
                    const clearValidationAndChange = (
                      value: React.ChangeEvent<HTMLTextAreaElement>
                    ) => {
                      clearErrors(ConfigurationFieldName.name);
                      dispatch(clearValidation({name: ConfigurationFieldName.name}));
                      onChange(value);
                    };

                    return (
                      <TextareaAutosize
                        onChange={clearValidationAndChange}
                        onBlur={onBlur}
                        value={value}
                        id="name"
                        className={`form-control required ${
                          errors.name || hasValidationMessage(validationErrors.name)
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder="Name"
                      />
                    );
                  }}
                />
                {errors.name && (
                  <div className="invalid-feedback">
                    {errors.name.type === "maxLength"
                      ? nameMaxLengthValidation()
                      : requiredFieldValidation}
                  </div>
                )}
                <ValidationMessage errors={validationErrors.name} />
              </div>

              <div className="form-group medium">
                <label htmlFor="description">Description</label>
                <Controller
                  control={control}
                  name={ConfigurationFieldName.description}
                  rules={{maxLength: descriptionMaxLength}}
                  render={({field: {onChange, onBlur, value}}) => {
                    const clearValidationAndChange = (
                      value: React.ChangeEvent<HTMLTextAreaElement>
                    ) => {
                      clearErrors(ConfigurationFieldName.description);
                      dispatch(clearValidation({name: ConfigurationFieldName.description}));
                      onChange(value);
                    };
                    return (
                      <TextareaAutosize
                        onChange={clearValidationAndChange}
                        onBlur={onBlur}
                        value={value}
                        id="description"
                        className={`form-control ${
                          errors.description || hasValidationMessage(validationErrors.description)
                            ? "is-invalid"
                            : ""
                        }`}
                        placeholder="Description"
                      />
                    );
                  }}
                />
                {errors.description && (
                  <div className="invalid-feedback">
                    {errors.description.type === "maxLength"
                      ? descriptionMaxLengthValidation()
                      : genericError}
                  </div>
                )}
                <ValidationMessage errors={validationErrors.description} />
              </div>

              <div className="form-group medium">
                <label htmlFor="FileInput" className="required">
                  Zip file
                </label>
                <input
                  type="file"
                  id="FileInput"
                  className={`form-control required ${
                    errors.file || hasValidationMessage(validationErrors.file) ? "is-invalid" : ""
                  }`}
                  {...register(ConfigurationFieldName.file, {
                    required: true,
                    validate: validateFileInput,
                    onChange: () => {
                      clearErrors(ConfigurationFieldName.file);
                      dispatch(clearValidation({name: ConfigurationFieldName.file}));
                    }
                  })}
                  placeholder="Upload a file"
                />
                {errors.file && (
                  <div className="invalid-feedback">
                    {errors.file.message || requiredFieldValidation}
                  </div>
                )}
                <ValidationMessage errors={validationErrors.file} />
              </div>
              <label>Applicability</label>
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th style={{whiteSpace: "nowrap"}} className="">
                      <div className="ellipsis-header">&nbsp;</div>
                    </th>
                    {userTypes.map(userType => (
                      <th key={userType.code} style={{whiteSpace: "nowrap"}} className="">
                        <div className="ellipsis-header applicability-column-header">
                          {userType.description}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allUserGroups.length > 0 ? (
                    allUserGroups.map(userGroup => (
                      <tr
                        key={userGroup.code}
                        className="stable-row"
                        id="configuration-details-table">
                        <td
                          className={`left-column ${
                            !userGroup.description ? "warning-highlight" : ""
                          }`}>
                          {userGroup.description
                            ? getFullPath(userGroup)
                            : defaultUserGroupDescription(userGroup.code)}
                        </td>
                        {userTypes.map(userType => {
                          const userTypeValidation = [
                            validationErrors.typeGroupPairs?.find(
                              error =>
                                error.parameters?.typeId === userType.code &&
                                error.parameters?.groupId === userGroup.code
                            )
                          ];

                          return (
                            <td
                              key={userType.code}
                              className={`regular-column ${
                                isPairUnavailable(unavailableTypeGroupPairs, userType, userGroup)
                                  ? "default-cursor"
                                  : ""
                              }`}>
                              <div className="form-group custom-control custom-checkbox small applicability-checkbox">
                                {isPairUnavailable(
                                  unavailableTypeGroupPairs,
                                  userType,
                                  userGroup
                                ) ? null : (
                                  <>
                                    <input
                                      type="checkbox"
                                      className={`custom-control-input ${
                                        hasValidationMessage(userTypeValidation) ? "is-invalid" : ""
                                      }`}
                                      id={`type_${userType.code}_group_${userGroup.code}`}
                                      {...register(
                                        `type_${userType.code}_group_${userGroup.code}`,
                                        {
                                          onChange: onUserTypeCheckboxChange(
                                            dispatch,
                                            userType,
                                            userGroup,
                                            clearValidation
                                          )
                                        }
                                      )}
                                    />
                                    <label
                                      className="custom-control-label"
                                      htmlFor={`type_${userType.code}_group_${userGroup.code}`}>
                                      &nbsp;
                                    </label>
                                    <ValidationMessage errors={userTypeValidation} />
                                  </>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  ) : (
                    <div className="no-available-user-groups">{noAvailableUserGroups}</div>
                  )}
                  <AddUserGroup
                    userTypes={userTypes}
                    userGroups={allUserGroups}
                    newUserGroups={newUserGroups}
                    setNewUserGroups={setNewUserGroupsWrapper}
                    setIsDirty={setIsAddUserGroupDirty}
                    isDirty={isAddUserGroupDirty}
                  />
                </tbody>
              </table>
              {hasAnyPairAvailable && (
                <button
                  type="button"
                  onClick={
                    areAllTypeGroupPairsSelected
                      ? deselectAll(
                          typeGroupEntries,
                          setValue,
                          clearValidationErrors,
                          unavailableTypeGroupPairs
                        )
                      : selectAll(typeGroupEntries, setValue, unavailableTypeGroupPairs)
                  }
                  className="btn btn-secondary btn-dark btn-select-deselect-all">
                  <span className="btn-select-deselect-all__text">
                    {areAllTypeGroupPairsSelected
                      ? "Deselect all applicabilities"
                      : "Select all applicabilities"}
                  </span>
                </button>
              )}
              {hasAnyPairAvailable && allUserGroups.some(userGroup => !userGroup.description) ? (
                <div className="warning-highlight applicability-access-error">
                  {applicablityAccessError}
                </div>
              ) : null}
              {(!hasAnyPairAvailable || hasLoadingApplicabilityFailed) &&
                hasLoadingApplicabilityFailed && (
                  <div>
                    <div className="block bg-sky-blue-lightest">
                      <div className="no-configurations">
                        <h4 className="no-configurations-text">
                          <span>{applicabilityMatrixError}</span>
                        </h4>
                      </div>
                    </div>
                  </div>
                )}
              <div className="buttons action-buttons">
                <Modal
                  title={"Discard changes"}
                  body={"Do you want to go back without saving changes?"}
                  onContinue={goBack}
                  disabled={!hasChanged || isLoading(status)}
                  showOnTabClose={true}
                  primaryButtonText="Discard">
                  <button
                    type="button"
                    onClick={isLoading(status) ? undefined : cancelChanges}
                    className="btn btn-secondary btn-dark btn-configuration-view">
                    Cancel
                  </button>
                </Modal>
                <SpinnerButton
                  onClick={isLoading(status) ? undefined : handleSubmit(onSubmit, onSubmitFailure)}
                  showSpinner={isLoading(status)}
                  className="btn-configuration-view">
                  Add Configuration
                </SpinnerButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
